/*
 * Glossary detail — SANCTIONED Class B popup exception to BO UI Standard §4 /
 * standing rule 12 (which mandates the full-page SettingsDetailPage). This
 * detail carries a nested "Translations" child-collection grid (Language +
 * Translation, with its own add/edit/delete) that the generic FLAT
 * SettingsDetailPage cannot express — the same category as MsrDetailDialog.
 * It still honours the save-flow essence: confirm → audited useSaveFlow →
 * shared "Saved successfully." (useNotify). Do NOT flatten into SettingsDetailPage.
 */
import { useLayoutEffect, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useConfirm, useSaveFlow } from '../../../components/confirm';
import {
  useGlossaryTranslations, useSaveGlossaryTranslation, useDeleteGlossaryTranslation,
  useSaveGlossaryDetail, GLOSSARY_LANGUAGES, errMessage,
  type GlossaryRow, type GlossaryTranslation,
} from './systemMasters';

interface Props { row: GlossaryRow; onClose: () => void; cardRef?: React.RefObject<HTMLDivElement | null>; }

const langName = (v: number) => GLOSSARY_LANGUAGES.find((l) => l.v === v)?.t ?? String(v);

/**
 * Glossary detail form — LIMS Glossary_DetailView. Field ID / Field Name / Table Name /
 * Module / BO are read-only; only Acronym + Definition are editable. A Translations child
 * grid (dbo.Translations) supports New / Edit / Delete. Save / Save and Close / Cancel.
 */
export function GlossaryDetailDialog({ row, onClose, cardRef }: Props) {
  const notify = useNotify();
  const { confirm, alert } = useConfirm();
  const runSave = useSaveFlow();
  const save = useSaveGlossaryDetail();
  // Opens MAXIMIZED filling the grid card (LIMS parity, same as the State/Department detail forms);
  // the restore toggle returns to the normal centred large dialog.
  const [maxed, setMaxed] = useState(true);
  const [maxRect, setMaxRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  useLayoutEffect(() => {
    if (!maxed) { setMaxRect(null); return; }
    const measure = () => {
      const r = cardRef?.current?.getBoundingClientRect();
      if (r) setMaxRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [maxed, cardRef]);
  const maxStyle = maxed && maxRect
    ? { position: 'fixed' as const, top: maxRect.top, left: maxRect.left, width: maxRect.width,
        height: maxRect.height, maxWidth: 'none' as const, maxHeight: 'none' as const, margin: 0 }
    : undefined;
  const [acronym, setAcronym] = useState(row.acronym ?? '');
  const [definition, setDefinition] = useState(row.definition ?? '');

  const { data: translations = [] } = useGlossaryTranslations(row.id);
  const saveTr = useSaveGlossaryTranslation(row.id);
  const delTr = useDeleteGlossaryTranslation(row.id);
  const [trSel, setTrSel] = useState<Set<string | number>>(new Set());
  const [trEdit, setTrEdit] = useState<{ id?: string; language: number; translation: string } | null>(null);
  const [trErr, setTrErr] = useState('');   // inline validation in the New/Edit Translation editor (§18.6)
  // New translations are STAGED in memory (LIMS commits the child collection on parent Save); a staged
  // row carries a "tmp-" id and cannot be Edited until the Glossary form is saved.
  const [staged, setStaged] = useState<GlossaryTranslation[]>([]);
  const isStaged = (id: string | number) => String(id).startsWith('tmp-');
  const trRows = [...translations, ...staged];
  const selStaged = trSel.size === 1 && isStaged([...trSel][0]!);

  // Shared save flow (§18.6): "Do you want to Save?" → save → "Saved successfully." → (optionally) close.
  const doSave = (close: boolean) => runSave({
    save: async () => {
      await save.mutateAsync({ id: row.id, acronym: acronym.trim() || null, definition: definition.trim() || null });
      // Commit any staged (new) translations here — LIMS commits the child collection on parent Save.
      if (staged.length) {
        for (const t of staged) await saveTr.mutateAsync({ language: t.language, translation: t.translation ?? '' });
        setStaged([]); setTrSel(new Set());
      }
    },
    successMessage: 'Saved successfully.',
    onSaved: () => { if (close) onClose(); },
    onError: (e: unknown) => notify.error(errMessage(e)),
  });

  const applyTr = async () => {
    if (!trEdit) return;
    const text = trEdit.translation.trim();
    if (!text) { setTrErr('Translation must not be empty.'); return; }
    // LIMS uniqueness rule: the same Language + Translation cannot repeat for this glossary
    // (checked across saved + staged rows so duplicates are caught before commit).
    if (trRows.some((t) => t.id !== trEdit.id && t.language === trEdit.language && (t.translation ?? '').trim() === text)) {
      setTrErr('This translation already exists.'); return;
    }
    setTrErr('');
    // New → STAGE in memory (committed on parent Save; its Edit stays disabled until then — LIMS parity).
    if (!trEdit.id) {
      const tmp: GlossaryTranslation = { id: `tmp-${crypto.randomUUID()}`, language: trEdit.language, translation: text };
      setStaged((s) => [...s, tmp]); setTrSel(new Set([tmp.id])); setTrEdit(null); return;
    }
    // Edit of an already-saved row → persist immediately (backend re-validates uniqueness).
    try { await saveTr.mutateAsync({ id: trEdit.id, language: trEdit.language, translation: text }); setTrSel(new Set([trEdit.id])); setTrEdit(null); }
    catch (e) { setTrErr(errMessage(e)); }
  };
  const delSelectedTr = async () => {
    if (trSel.size === 0) { notify.warning('Select a translation to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${trSel.size} translation${trSel.size === 1 ? '' : 's'}?` })) return;
    const ids = [...trSel].map(String);
    try {
      // Staged (unsaved) rows are just dropped from memory; saved rows are deleted server-side.
      const stagedIds = ids.filter(isStaged);
      if (stagedIds.length) setStaged((s) => s.filter((t) => !stagedIds.includes(t.id)));
      for (const id of ids.filter((i) => !isStaged(i))) await delTr.mutateAsync(id);
      setTrSel(new Set());
      // Same centred success dialog as the save flow (NOT a top toast) — consistent placement.
      await alert({ title: 'Success', message: 'Deleted successfully.', tone: 'success' });
    } catch (e) { notify.error(errMessage(e)); }
  };
  const openTrEdit = (v: { id?: string; language: number; translation: string }) => { setTrErr(''); setTrEdit(v); };
  const editSelectedTr = () => {
    const id = [...trSel][0]; if (!id || isStaged(id)) return;   // unsaved rows can't be edited until parent Save (LIMS)
    const t = trRows.find((x) => x.id === id); if (t) openTrEdit({ id: t.id, language: t.language, translation: t.translation ?? '' });
  };

  const trCols: AppColumn<GlossaryTranslation>[] = [
    // Per-row Edit pencil (LIMS parity) — opens the Edit Translation dialog directly, no selection needed.
    { field: 'edit', header: '', sortable: false, filterable: false, width: 40, align: 'center',
      // Unsaved (staged) rows: pencil disabled until the Glossary form is saved (LIMS parity).
      render: (t) => isStaged(t.id)
        ? <button className="pts-icon-btn edit" disabled title="Save the Glossary to edit this translation"><i className="ti ti-edit" /></button>
        : <button className="pts-icon-btn edit" title="Edit" onClick={(e) => { e.stopPropagation(); openTrEdit({ id: t.id, language: t.language, translation: t.translation ?? '' }); }}><i className="ti ti-edit" /></button> },
    { field: 'language', header: 'Language', width: '40%', minWidth: 140, sortable: false, text: (r) => langName(r.language) },
    { field: 'translation', header: 'Translation', width: '60%', minWidth: 200, sortable: false, text: (r) => r.translation ?? '' },
  ];

  return (
    <div className="acc-overlay" onMouseDown={onClose}>
      <div className={`acc-dialog acc-dialog-lg gloss-detail${maxed ? ' acc-dialog-max' : ''}`} style={maxStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div className="acc-dialog-hdr">
          <span>Glossary - {row.fieldName ?? row.fieldId}</span>
          <span className="acc-dialog-acts">
            <i className={`ti ${maxed ? 'ti-minimize' : 'ti-maximize'} acc-dialog-x`} title={maxed ? 'Restore' : 'Maximize'} onClick={() => setMaxed((m) => !m)} />
            <i className="ti ti-x acc-dialog-x" onClick={onClose} />
          </span>
        </div>
        <div className="acc-dialog-body">
          <div className="gloss-form">
            <Field label="Field ID"><input className="acc-input gloss-ro" value={row.fieldId ?? ''} readOnly /></Field>
            <Field label="Field Name" req><input className="acc-input gloss-ro" value={row.fieldName ?? ''} readOnly /></Field>
            <Field label="Acronym"><input className="acc-input" value={acronym} onChange={(e) => setAcronym(e.target.value)} /></Field>
            <Field label="Definition"><textarea className="acc-input gloss-def" rows={2} value={definition} onChange={(e) => setDefinition(e.target.value)} /></Field>
            <Field label="Table Name"><input className="acc-input gloss-ro" value={row.tableName ?? ''} readOnly /></Field>
            {/* Module / BO are read-only comboboxes in LIMS — disabled selects show the value + dropdown arrow. */}
            <Field label="Module" req><select className="acc-input gloss-ro" disabled><option>{row.module ?? ''}</option></select></Field>
            <Field label="BO" req><select className="acc-input gloss-ro" disabled><option>{row.bo ?? ''}</option></select></Field>
          </div>

          <div className="gloss-tr">
            <div className="acc-sub-title">Translations</div>
            <div className="acc-sub-toolbar">
              <button className="ca-btn" onClick={() => openTrEdit({ language: 0, translation: '' })}><i className="ti ti-plus" /> New</button>
              <button className="ca-btn" onClick={editSelectedTr} disabled={trSel.size !== 1 || selStaged}><i className="ti ti-edit" /> Edit</button>
              <button className="ca-btn" onClick={() => void delSelectedTr()} disabled={trSel.size === 0}><i className="ti ti-trash" /> Delete</button>
              <ExportMenu triggerClassName="ca-btn" filename="Glossary-Translations" headers={['Language', 'Translation']}
                rows={trRows.map((t) => [langName(t.language), t.translation ?? ''])} />
            </div>
            <AppDataGrid<GlossaryTranslation> rows={trRows} columns={trCols} getRowId={(r) => r.id}
              selectable selectedIds={trSel} onSelectionChange={setTrSel}
              emptyText="No data to display." defaultPageSize={20} />
          </div>
        </div>
        <div className="acc-dialog-ftr">
          <button className="ca-btn ca-btn-solid" onClick={() => void doSave(false)} disabled={save.isPending}><span className="tb-i">💾</span> Save</button>
          <button className="ca-btn" onClick={() => void doSave(true)} disabled={save.isPending}><span className="tb-i">💾</span> Save and Close</button>
          <button className="ca-btn" onClick={onClose} disabled={save.isPending}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
        </div>
      </div>

      {trEdit && (
        <div className="acc-overlay" onMouseDown={() => setTrEdit(null)}>
          <div className="acc-dialog gloss-tr-edit" onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr"><span>{trEdit.id ? 'Edit Translation' : 'New Translation'}</span><i className="ti ti-x acc-dialog-x" onClick={() => setTrEdit(null)} /></div>
            <div className="acc-dialog-body">
              {/* Language = radio group (Chinese / French / Spanish), single-line Translation — LIMS parity. */}
              <Field label="Language" req tall>
                <div className="gloss-radio-group">
                  {GLOSSARY_LANGUAGES.map((l) => (
                    <label key={l.v} className="gloss-radio">
                      <input type="radio" name="gloss-tr-lang" checked={trEdit.language === l.v} onChange={() => { setTrErr(''); setTrEdit({ ...trEdit, language: l.v }); }} />
                      {l.t}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Translation" req><input className="acc-input" value={trEdit.translation} onChange={(e) => { setTrErr(''); setTrEdit({ ...trEdit, translation: e.target.value }); }} /></Field>
              {trErr && <div className="gloss-tr-err">{trErr}</div>}
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void applyTr()}><span className="tb-i">💾</span> OK</button>
              <button className="ca-btn" onClick={() => setTrEdit(null)}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, req, tall, children }: { label: string; req?: boolean; tall?: boolean; children: React.ReactNode }) {
  return (
    <div className={`acc-field-h${tall ? ' gloss-field-tall' : ''}`}>
      <label className={`acc-label${req ? ' gloss-req' : ''}`}>{label}{req && <span className="gloss-star">*</span>}</label>
      <div className="acc-fh-control">{children}</div>
    </div>
  );
}
